import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "logoInput",
    "unsavedChangesModal",
    "unsavedChangesLeave",
    "unsavedChangesStay",
    "masterToggle"
  ]

  connect() {
    this.suppressUnsavedWarning = false
    this.navigatingAfterConfirmation = false

    this.formElement = this.formTarget
    this.initialFormState = this.normalizeFormData(new FormData(this.formElement))

    // append dialogs to end of body for proper styling in all browsers
    this.modal = this.unsavedChangesModalTarget
    if (this.modal && !document.body.contains(this.modal)) {
      document.body.appendChild(this.modal)
    }

    this.initializeMasterToggles()

    this.initialLogoFileCount = this.hasLogoInputTarget ? this.logoInputTarget.files.length : 0

    this.setupEventListeners()
  }

  normalizeFormData(formData) {
    const normalized = new Map()
    const checkboxNames = new Set()
    
    this.formElement.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      if (checkbox.name) {
        checkboxNames.add(checkbox.name)
      }
    })

    for (const [key, value] of formData.entries()) {
      if (checkboxNames.has(key)) {
        const values = formData.getAll(key)
        normalized.set(key, values.includes("1") ? "true" : "false")
      } else {
        normalized.set(key, value)
      }
    }

    return normalized
  }

  initializeMasterToggles() {
    const animationDuration = 300

    this.masterToggleTargets.forEach((toggle) => {
      const $toggle = $(toggle)
      const targetClass = $toggle.data('toggle-target')
      const fieldName = $toggle.data('field-name')

      if (!fieldName) return

      const isChecked = $toggle.is(':checked')
      this.element.querySelectorAll('.' + targetClass).forEach((target) => {
        if (isChecked) {
          $(target).show()
        } else {
          $(target).hide()
        }
      })

      $toggle.on('change', () => {
        const checked = $toggle.is(':checked')

        this.element.querySelectorAll('.' + targetClass).forEach((target) => {
          if (checked) {
            $(target).slideDown(animationDuration)
          } else {
            $(target).slideUp(animationDuration)
          }
        })
      })
    })
  }

  setupEventListeners() {
    this.formElement.addEventListener('submit', () => {
      this.suppressUnsavedWarning = true
    })

    document.addEventListener('click', (e) => {
      const link = e.target.closest('.mdl-navigation a, .mdl-navigation__link a, .mdl-breadcrumb__list-item a')
      if (!link) return

      const url = this.extractUrlFromElement(link)
      if (!url) return

      if (!this.handleNavigation(e, url)) {
        return true
      }
    })

    window.addEventListener('keydown', (e) => {
      const isRefreshShortcut = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'r')
      if (!isRefreshShortcut || !this.shouldWarn()) return

      e.preventDefault()
      e.stopPropagation()

      this.showModal().then((confirm) => {
        if (!confirm) return

        this.suppressUnsavedWarning = true
        this.navigatingAfterConfirmation = true
        window.location.reload()
      })
    })

    window.addEventListener('beforeunload', (e) => {
      if (!this.navigatingAfterConfirmation && this.shouldWarn()) {
        e.preventDefault()
      }
    })
  }

  hasFormChanged() {
    if (!this.initialFormState || !this.formElement) return false

    const currentState = this.normalizeFormData(new FormData(this.formElement))
    const allKeys = new Set()

    for (const key of currentState.keys()) {
      allKeys.add(key)
    }
    for (const key of this.initialFormState.keys()) {
      allKeys.add(key)
    }

    const fileInputNames = new Set()
    this.formElement.querySelectorAll('input[type="file"]').forEach((input) => {
      if (input.name) fileInputNames.add(input.name)
    })

    for (const key of allKeys) {
      if (fileInputNames.has(key)) continue

      const currentValue = currentState.get(key)
      const initialValue = this.initialFormState.get(key)

      if (currentValue !== initialValue) {
        return true
      }
    }

    return false
  }

  hasLogoInputChanged() {
    if (!this.hasLogoInputTarget) return false
    return this.logoInputTarget.files.length !== this.initialLogoFileCount
  }

  shouldWarn() {
    if (this.suppressUnsavedWarning) return false
    return this.hasFormChanged() || this.hasLogoInputChanged()
  }


  extractUrlFromElement(el) {
    const href = el.getAttribute('href')
    if (href && !href.startsWith('#') && !href.startsWith('javascript:')) {
      return href
    }
    return null
  }

  handleNavigation(e, url) {
    if (!url || !this.shouldWarn()) return true

    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()

    this.showModal().then((confirm) => {
      if (confirm) this.navigateTo(url)
    })

    return false
  }

  navigateTo(url) {
    this.suppressUnsavedWarning = true
    this.navigatingAfterConfirmation = true
    window.location.href = url
  }

  showModal() {
    return new Promise((resolve) => {
      if (!this.modal) return resolve(true)

      const leaveButton = this.unsavedChangesLeaveTarget
      const stayButton = this.unsavedChangesStayTarget

      const handleClick = (e) => {
        this.modal.close()
        resolve(e.currentTarget.id === leaveButton.id)
        leaveButton.removeEventListener('click', handleClick)
        stayButton.removeEventListener('click', handleClick)
      }

      leaveButton.addEventListener('click', handleClick)
      stayButton.addEventListener('click', handleClick)
      this.modal.showModal()
    })
  }
}

